import type { PomodoroState } from "../hooks/use-pomodoro";
import { TimerModule } from "../../../ui/molecules/TimerModule";

interface PomodoroTimerProps {
  readonly state: PomodoroState;
  readonly onStart: () => void;
  readonly onPause: () => void;
  readonly onReset: () => void;
  readonly onSkip: () => void;
  readonly onSettings: () => void;
}

const MODE_LABELS: Record<PomodoroState["mode"], string> = {
  work: "Travail",
  break: "Pause",
  longBreak: "Longue pause",
};

const MODE_COLORS: Record<PomodoroState["mode"], string> = {
  work: "text-accent",
  break: "text-success",
  longBreak: "text-ink-muted",
};

const MAX_DOTS = 8;

function SessionDots({ count }: { readonly count: number }) {
  if (count <= 0) return null;
  const dots = Math.min(count, MAX_DOTS);
  const overflow = count > MAX_DOTS;

  return (
    <div className="flex items-center gap-1 mt-0.5">
      {Array.from({ length: dots }).map((_, i) => (
        <div key={i} className="w-1.5 h-1.5 rounded-full bg-accent" />
      ))}
      {overflow && (
        <span className="text-[10px] text-ink-muted font-mono">+{count - MAX_DOTS}</span>
      )}
    </div>
  );
}

function formatTimeDisplay(timeLeft: number): string {
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function PomodoroTimer({
  state,
  onStart,
  onPause,
  onReset,
  onSkip,
  onSettings,
}: PomodoroTimerProps) {
  return (
    <TimerModule
      isRunning={state.isRunning}
      timeDisplay={formatTimeDisplay(state.timeLeft)}
      timeColorClass={MODE_COLORS[state.mode]}
      onStart={onStart}
      onPause={onPause}
      onReset={onReset}
      onSettings={onSettings}
      settingsLabel="Réglages Pomodoro"
      info={
        <>
          <span className="text-xs font-semibold text-ink-muted uppercase tracking-widest">
            Pomodoro
          </span>
          <span className={`text-xs font-medium ${MODE_COLORS[state.mode]}`}>
            {MODE_LABELS[state.mode]}
          </span>
          <SessionDots count={state.counter} />
        </>
      }
      extraActions={[
        {
          label: "⏭",
          ariaLabel: "Sauter",
          onClick: onSkip,
        },
      ]}
    />
  );
}
